import React, {useState, ChangeEvent, useEffect} from 'react';
import et from 'date-fns/locale/et';
import {useAppDispatch, useAppSelector} from "../../../utils/Hooks";
import {useLocation, useNavigate} from "react-router-dom";
import {getCountries} from "../../../store/reducers/CountriesSlice";
import {getServices} from "../../../store/reducers/ServicesSlice";
import {getUsers} from "../../../store/reducers/UsersSlice";
import {Option} from "../../common/form/ElogSelect";
import DatePicker, {registerLocale} from "react-datepicker";
import dayjs from "dayjs";
import {apiClient} from "../../../utils/ApiClient";
import Autosuggest, {SuggestionsFetchRequestedParams} from "react-autosuggest";
import {API_URL} from "../../../utils/Constants";

registerLocale('et', et)

const OrdersSearch: React.FC = () => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {search} = useLocation();

    const usersState = useAppSelector((state) => state.users);
    const countriesState = useAppSelector(state => state.countries);
    const servicesState = useAppSelector((state) => state.services);

    const [startCountry, setStartCountry] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>('');
    const [fromDateObject, setFromDateObject] = useState<Date>(new Date());
    const [toDate, setToDate] = useState<string>('');
    const [toDateObject, setToDateObject] = useState<Date>(new Date());
    const [user, setUser] = useState<string>('');
    const [checker, setChecker] = useState<string>('');
    const [serviceType, setServiceType] = useState<string>('');
    const [destinationCountry, setDestinationCountry] = useState<string>('');
    const [status, setStatus] = useState<string>('all');
    const [perPage, setPerPage] = useState<string>('80');
    const [needInvoice, setNeedInvoice] = useState<boolean>(false);
    const [vendor, setVendor] = useState<string>('');
    const [client, setClient] = useState<string>('');
    const [amountFrom, setAmountFrom] = useState<string>('');
    const [amountTo, setAmountTo] = useState<string>('');
    const [invoiceNumber, setInvoiceNumber] = useState<string>('');
    const [buyInvoice, setBuyInvoice] = useState<string>('');
    const [id, setId] = useState<string>('');

    const [clientSuggestions, setClientSuggestions] = useState<Option[]>([]);
    const [vendorSuggestions, setVendorSuggestions] = useState<Option[]>([]);

    const parseDate = function (dateString: string): Date {
        const [day, month, year] = dateString.split('.');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    useEffect(() => {

        if ( fromDate?.length > 0 ) {
            setFromDateObject(parseDate(fromDate));
        }

    },[fromDate]);

    useEffect(() => {

        if ( toDate?.length > 0 ) {
            setToDateObject(parseDate(toDate));
        }

    },[toDate])

    const fetchClients = async (inputValue: string) => {
        try {
            const apiResponse = await apiClient.get(`suggest/client?search=${encodeURIComponent(inputValue)}`);
            const clients: Option[] = apiResponse.data.map((client: { id: number; name: string }) => ({
                label: client.name,
                value: client.id.toString(),
            }));
            setClientSuggestions(clients);
        } catch (error) {
            console.error('Fetch clients error:', error);
        }
    };

    const fetchVendors = async (inputValue: string) => {
        try {
            const apiResponse = await apiClient.get(`suggest/vendor?name=${encodeURIComponent(inputValue)}`);
            const clients: Option[] = apiResponse.data.map((vendor: { id: number; name: string }) => ({
                label: vendor.name,
                value: vendor.id.toString(),
            }));
            setVendorSuggestions(clients);
        } catch (error) {
            console.error('Fetch vendors error:', error);
        }
    };

    const onClientSuggestionsFetchRequested = ({value}: SuggestionsFetchRequestedParams) => {
        if (value.length > 1) {
            fetchClients(value);
        } else {
            setClientSuggestions([]);
        }
    };

    const onVendorSuggestionsFetchRequested = ({value}: SuggestionsFetchRequestedParams) => {
        if (value.length > 1) {
            fetchVendors(value);
        } else {
            setVendorSuggestions([]);
        }
    };

    const onClientSuggestionsClearRequested = () => {
        setClientSuggestions([]);
    };

    const onVendorSuggestionsClearRequested = () => {
        setVendorSuggestions([]);
    };

    const onClientSuggestionSelected = (event: React.FormEvent<any>, {suggestion}: { suggestion: Option }) => {
        setClient(suggestion.label);
    };

    const onVendorSuggestionSelected = (event: React.FormEvent<any>, {suggestion}: { suggestion: Option }) => {
        setVendor(suggestion.label);
    };

    const renderSuggestion = (suggestion: Option) => (
        <div>
            {suggestion.label}
        </div>
    );

    useEffect(() => {

        dispatch(getCountries());
        dispatch(getServices(false));
        dispatch(getUsers());

        const urlParams = new URLSearchParams(window.location.search);

        setStartCountry(urlParams.get("startCountry") || '');
        setFromDate(urlParams.get("fromDate") || '');
        setToDate(urlParams.get("toDate") || '');
        setUser(urlParams.get("user") || '');
        setChecker(urlParams.get("checker") || '');
        setServiceType(urlParams.get("serviceType") || '');
        setDestinationCountry(urlParams.get("destinationCountry") || '');
        setStatus(urlParams.get("status") || 'all');
        setPerPage(urlParams.get("perPage") || '80');
        setNeedInvoice(urlParams.get("needInvoice") === "1");
        setVendor(urlParams.get("vendor") || '');
        setClient(urlParams.get("client") || '');
        setAmountFrom(urlParams.get("amountFrom") || '');
        setAmountTo(urlParams.get("amountTo") || '');
        setInvoiceNumber(urlParams.get("invoiceNumber") || '');
        setBuyInvoice(urlParams.get("buyInvoice") || '');
        setId(urlParams.get("id") || '');

    }, []);

    const handleSearch = () => {

        const searchParams = new URLSearchParams();

        searchParams.set("startCountry", startCountry);
        searchParams.set("fromDate", fromDate);
        searchParams.set("toDate", toDate);
        searchParams.set("user", user);
        searchParams.set("checker", checker);
        searchParams.set("serviceType", serviceType);
        searchParams.set("destinationCountry", destinationCountry);
        searchParams.set("status", status);
        searchParams.set("perPage", perPage);
        searchParams.set("needInvoice", needInvoice ? "1" : "0");
        searchParams.set("vendor", vendor);
        searchParams.set("client", client);
        searchParams.set("amountFrom", amountFrom || "");
        searchParams.set("amountTo", amountTo || "");
        searchParams.set("invoiceNumber", invoiceNumber);
        searchParams.set("buyInvoice", buyInvoice);
        searchParams.set("id", id);

        const newUrl = `/orders?${searchParams.toString()}`;
        navigate(newUrl);

    };

    const handleExcelDownload = () => {


    };

    const userOptions: Option[] = usersState.users
        .filter((user) => user.logistician === 1)
        .map((user) => ({
            value: user.id.toString(),
            label: `${user.firstname} ${user.lastname}`,
        }));

    const checkerOptions: Option[] = usersState.users
        .filter((user) => user.is_checker === 1)
        .map((user) => ({
            value: user.id.toString(),
            label: `${user.firstname} ${user.lastname}`,
        }));

    const countryOptions: Option[] = [
        {value: '0', label: 'Kõik'},
        ...countriesState.countries.map((country) => ({
            value: country.id.toString(),
            label: country.name,
        })),
    ];

    const serviceOptions: Option[] = [
        {value: '0', label: 'Kõik'},
        ...servicesState.services.map((service) => ({
            value: service.id.toString(),
            label: service.name,
        })),
    ];

    const accessToken = localStorage.getItem('token');

    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="filter clear" style={{width: '99%'}} onKeyPress={handleKeyPress}>
            <div className="content" style={{width: '100%'}}>
                <p>
                    <label><strong>Alguskoht:</strong></label>
                    <select
                        name="start_country"
                        className="w150"
                        value={startCountry}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setStartCountry(e.target.value)}
                    >
                        {countryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    &nbsp;&nbsp;&nbsp;
                    <span style={{display: 'inline-flex'}}>
                        <label><strong>Periood:</strong></label>
                        <DatePicker
                            key={'from'}
                            value={fromDate}
                            selected={fromDateObject}
                            focusSelectedMonth={true}
                            onChange={(date) => setFromDate(date ? dayjs(date).format('DD.MM.YYYY') : '')}
                            dateFormat={'dd.MM.yyyy'}
                            className={'date'}
                            locale="et"
                            showYearDropdown={true}
                            showMonthDropdown={true}
                        />
                        –
                        <DatePicker
                            key={'to'}
                            value={toDate}
                            selected={toDateObject}
                            focusSelectedMonth={true}
                            onChange={(date) => setToDate(date ? dayjs(date).format('DD.MM.YYYY') : '')}
                            dateFormat={'dd.MM.yyyy'}
                            className={'date'}
                            locale="et"
                            showYearDropdown={true}
                            showMonthDropdown={true}
                        />
                        </span>
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Logistik:</strong></label>
                    <select
                        name="user"
                        className="w150"
                        value={user}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setUser(e.target.value)}
                    >
                        <option value="">Kõik</option>
                        {userOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Teenuse liik:</strong></label>
                    <select
                        name="service"
                        className="w150"
                        value={serviceType}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setServiceType(e.target.value)}
                    >
                        {serviceOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </p>
                <p>
                    <label><strong>Sihtkoht:&nbsp;&nbsp;&nbsp;&nbsp;</strong></label>
                    <select
                        name="destination_country"
                        className="w150"
                        value={destinationCountry}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setDestinationCountry(e.target.value)}
                    >
                        {countryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Kirjeid lehel:</strong></label>
                    <input
                        style={{width: '50px'}}
                        type="text"
                        name="perpage"
                        value={perPage}
                        onChange={(e) => setPerPage(e.target.value)}
                    />
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Arve number:</strong></label>
                    <input
                        style={{width: '90px'}}
                        type="text"
                        name="perpage"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                    />
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Ostuarve:</strong></label>
                    <input
                        style={{width: '90px'}}
                        type="text"
                        name="buyInvoice"
                        value={buyInvoice}
                        onChange={(e) => setBuyInvoice(e.target.value)}
                    />
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>ID:</strong></label>
                    <input
                        style={{width: '90px'}}
                        type="text"
                        name="perpage"
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                    />
                </p>
                <p>
                        <span style={{display: 'inline-flex'}}>
                        <label>
                            <strong>Hankija:&nbsp;&nbsp;&nbsp;&nbsp;</strong>
                        </label>
                        <Autosuggest
                            suggestions={vendorSuggestions}
                            onSuggestionsFetchRequested={onVendorSuggestionsFetchRequested}
                            onSuggestionsClearRequested={onVendorSuggestionsClearRequested}
                            onSuggestionSelected={onVendorSuggestionSelected}
                            getSuggestionValue={(suggestion: Option) => suggestion.label}
                            renderSuggestion={renderSuggestion}
                            inputProps={{
                                value: vendor,
                                onChange: (_, {newValue}) => {
                                    setVendor(newValue);
                                },
                            }}
                            theme={{
                                container: 'react-autosuggest__container',
                                suggestionsContainer: (vendorSuggestions.length > 0)
                                    ? 'react-autosuggest__suggestions-container visible'
                                    : 'react-autosuggest__suggestions-container',
                                suggestionsList: 'react-autosuggest__suggestions-list',
                                suggestion: 'react-autosuggest__suggestion',
                                suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                            }}
                        />
                        </span>
                    &nbsp;&nbsp;&nbsp;
                    <span style={{display: 'inline-flex'}}>
                        <label>
                            <strong>Klient:&nbsp;&nbsp;&nbsp;&nbsp;</strong>
                        </label>
                        <Autosuggest
                            suggestions={clientSuggestions}
                            onSuggestionsFetchRequested={onClientSuggestionsFetchRequested}
                            onSuggestionsClearRequested={onClientSuggestionsClearRequested}
                            onSuggestionSelected={onClientSuggestionSelected}
                            getSuggestionValue={(suggestion: Option) => suggestion.label}
                            renderSuggestion={renderSuggestion}
                            inputProps={{
                                value: client,
                                onChange: (_, {newValue}) => {
                                    setClient(newValue);
                                },
                            }}
                            theme={{
                                container: 'react-autosuggest__container',
                                suggestionsContainer: (clientSuggestions.length > 0)
                                    ? 'react-autosuggest__suggestions-container visible'
                                    : 'react-autosuggest__suggestions-container',
                                suggestionsList: 'react-autosuggest__suggestions-list',
                                suggestion: 'react-autosuggest__suggestion',
                                suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                            }}
                        />
                        </span>
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Kogus:</strong></label>
                    <input
                        type="number"
                        style={{width: '40px'}}
                        step="0.1"
                        name="amount_from"
                        autoComplete={'off'}
                        value={amountFrom}
                        onChange={(e) => setAmountFrom(e.target.value)}
                    />
                    ldm –
                    <input
                        type="number"
                        style={{width: '40px'}}
                        step="0.1"
                        name="amount_to"
                        autoComplete={'off'}
                        value={amountTo}
                        onChange={(e) => setAmountTo(e.target.value)}
                    /> ldm
                    &nbsp;&nbsp;&nbsp;
                    <label><strong>Checker:</strong></label>
                    <select
                        name="checker"
                        className="w150"
                        value={checker}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setChecker(e.target.value)}
                    >
                        <option value="">Kõik</option>
                        {checkerOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </p>
            </div>
            <p className="action">
                <button type="submit" onClick={handleSearch}><span><strong>Otsi</strong></span></button>
                <a
                    target="_blank"
                    className="filter-secondary"
                    href={`${API_URL}orders/export?token=${encodeURIComponent(accessToken || '')}&startCountry=${encodeURIComponent(startCountry)}&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}&user=${encodeURIComponent(user)}&checker=${encodeURIComponent(checker)}&serviceType=${encodeURIComponent(serviceType)}&destinationCountry=${encodeURIComponent(destinationCountry)}&status=${encodeURIComponent(status)}&perPage=${encodeURIComponent(perPage)}&vendor=${encodeURIComponent(vendor)}&client=${encodeURIComponent(client)}&amountFrom=${encodeURIComponent(amountFrom)}&amountTo=${encodeURIComponent(amountTo)}&invoiceNumber=${encodeURIComponent(invoiceNumber)}&buyInvoice=${encodeURIComponent(buyInvoice)}&id=${encodeURIComponent(id)}`}
                    onClick={handleExcelDownload}
                >
                    Excel download
                </a>
            </p>
        </div>
    );
};

export default OrdersSearch;